import * as React from "react";

/**
 * Copy feedback shared by the v2 copy buttons (AiAnswerBlock, InfoRow, chat
 * messages; redesign phase 5, #455). The caller shows the state in the
 * button's label ("Kopiert" / "Kopieren fehlgeschlagen") and says it in a
 * polite status. No toast (Toast rules, 04).
 *
 * `copy(text)` runs `onCopy` or `navigator.clipboard.writeText`, awaited. A
 * throw, a rejection or a missing clipboard (add-in iframe, insecure context)
 * is a failure. Success and failure each show for 2 s; a new click restarts
 * the clock. Resolves to whether the copy worked.
 */

export type CopyState = "idle" | "copied" | "failed";

export const COPY_FEEDBACK_MS = 2000;

export function useCopyFeedback(onCopy?: (text: string) => void | Promise<void>) {
  const [state, setState] = React.useState<CopyState>("idle");
  const timer = React.useRef<number | undefined>(undefined);
  React.useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = async (text: string): Promise<boolean> => {
    window.clearTimeout(timer.current);
    let ok = true;
    try {
      if (onCopy) {
        await onCopy(text);
      } else {
        if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
        await navigator.clipboard.writeText(text);
      }
    } catch {
      ok = false;
    }
    setState(ok ? "copied" : "failed");
    timer.current = window.setTimeout(() => setState("idle"), COPY_FEEDBACK_MS);
    return ok;
  };

  return { state, copy };
}
